import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabase/supabase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Prevent repeated profile fetch for same user
  const lastFetchedUserIdRef = useRef(null);
  const isFetchingProfileRef = useRef(false);

  /**
   * resolveAvatarUrl - properly constructs the path for avatars/<userId>/<filename>
   */
  const resolveAvatarUrl = useCallback((filename, userFolderId) => {
    if (!filename) return null;

    // if already a full url, return it
    if (typeof filename === 'string' && /^(http|https):\/\//i.test(filename)) {
      return filename;
    }
    // if data url, return it
    if (typeof filename === 'string' && filename.startsWith('data:')) return filename;

    // Construct the proper path: avatars/<userId>/<filename>
    const path = `${userFolderId}/${filename}`;
    
    try {
      const { data } = supabase.storage.from('avatars').getPublicUrl(path);
      if (data?.publicUrl) {
        return data.publicUrl;
      }
    } catch (e) {
      console.error('[AuthContext] Error resolving avatar URL:', e);
    }

    return null;
  }, []);

  /**
   * fetchAndSetProfile - robust profile fetching with fallbacks
   */
  const fetchAndSetProfile = useCallback(async (user) => {
    if (!user) {
      lastFetchedUserIdRef.current = null;
      isFetchingProfileRef.current = false;
      setUserProfile(null);
      try { localStorage.removeItem('supabase_profile'); } catch (_) {}
      return null;
    }

    // avoid re-fetching the same user repeatedly
    if (lastFetchedUserIdRef.current === user.id && userProfile) {
      return userProfile;
    }

    // prevent concurrent fetches
    if (isFetchingProfileRef.current) {
      return userProfile;
    }

    isFetchingProfileRef.current = true;
    console.log('[AuthContext] Fetching profile for user:', user.id);

    try {
      // Try to fetch profile from Supabase
      let profile = null;
      
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('display_name, username, id, user_id, avatar_url')
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (error) {
          console.log('[AuthContext] Profile lookup error:', error);
        } else if (data) {
          profile = data;
          console.log('[AuthContext] Profile found:', profile);
        }
      } catch (qErr) {
        console.log('[AuthContext] Profile query failed:', qErr);
      }

      // If no profile found, create fallback from user metadata
      if (!profile) {
        console.log('[AuthContext] No profile found, using metadata fallback');
        profile = {
          id: user.id,
          display_name: user.user_metadata?.full_name || user.user_metadata?.name || (user.email ? user.email.split('@')[0] : 'User'),
          avatar_url: user.user_metadata?.avatar_url || null,
          username: user.user_metadata?.user_name || null,
        };
      }

      // Resolve avatar URL
      const avatarFromProfile = profile?.avatar_url || null;
      const avatarFromMeta = user.user_metadata?.avatar_url || user.user_metadata?.picture || null;
      const folderId = user.id;
      
      let resolvedAvatar = null;
      if (avatarFromProfile) {
        resolvedAvatar = resolveAvatarUrl(avatarFromProfile, folderId);
      }
      if (!resolvedAvatar && avatarFromMeta) {
        resolvedAvatar = resolveAvatarUrl(avatarFromMeta, folderId);
      }
      if (!resolvedAvatar) {
        // Use default avatar data URL
        resolvedAvatar = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiNmM2Y0ZjYiLz4KPHN2ZyB4PSIxMCIgeT0iMTAiIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM5Y2EzYWYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj4KPHBhdGggZD0iTTIwIDIxdi0yYTQgNCAwIDAgMC00LTRIOGE0IDQgMCAwIDAtNCA0djIiLz4KPGNpcmNsZSBjeD0iMTIiIGN5PSI3IiByPSI0Ii8+Cjwvc3ZnPgo8L3N2Zz4K';
      }

      const displayName = profile?.display_name || profile?.username || user.user_metadata?.full_name || user.user_metadata?.name || 'User';

      const resolvedProfile = { 
        display_name: displayName, 
        avatar_url: resolvedAvatar, 
        email: user.email, 
        id: user.id,
        username: profile?.username || user.user_metadata?.user_name || null
      };
      
      setUserProfile(resolvedProfile);
      try { localStorage.setItem('supabase_profile', JSON.stringify(resolvedProfile)); } catch (_) {}
      lastFetchedUserIdRef.current = user.id;
      isFetchingProfileRef.current = false;

      console.log('[AuthContext] Profile set successfully:', { userId: user.id, displayName, hasAvatar: !!resolvedAvatar });
      return resolvedProfile;
    } catch (err) {
      console.error('[AuthContext] Profile fetch failed:', err);
      const fallbackName = user.user_metadata?.full_name || user.user_metadata?.name || 'User';
      const fallbackAvatarMeta = user.user_metadata?.avatar_url || user.user_metadata?.picture || null;
      const fallbackResolved = resolveAvatarUrl(fallbackAvatarMeta, user.id) || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiNmM2Y0ZjYiLz4KPHN2ZyB4PSIxMCIgeT0iMTAiIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM5Y2EzYWYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj4KPHBhdGggZD0iTTIwIDIxdi0yYTQgNCAwIDAgMC00LTRIOGE0IDQgMCAwIDAtNCA0djIiLz4KPGNpcmNsZSBjeD0iMTIiIGN5PSI3IiByPSI0Ii8+Cjwvc3ZnPgo8L3N2Zz4K';
      const resolvedProfile = { 
        display_name: fallbackName, 
        avatar_url: fallbackResolved, 
        email: user.email, 
        id: user.id,
        username: user.user_metadata?.user_name || null
      };
      setUserProfile(resolvedProfile);
      try { localStorage.setItem('supabase_profile', JSON.stringify(resolvedProfile)); } catch (_) {}
      lastFetchedUserIdRef.current = user.id;
      isFetchingProfileRef.current = false;
      return resolvedProfile;
    }
  }, [resolveAvatarUrl, userProfile]);

  // Initialize authentication state
  useEffect(() => {
    let mounted = true;

    const init = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (error) {
          console.log('[AuthContext] getSession error:', error);
        }
        let user = data?.session?.user || null;
        
        // Fallback: check localStorage if no session
        if (!user) {
          const storedUser = localStorage.getItem('supabase_user');
          if (storedUser) {
            try {
              const parsedUser = JSON.parse(storedUser);
              console.log('[AuthContext] Using stored user from localStorage');
              user = parsedUser;
            } catch (e) {
              console.log('[AuthContext] Failed to parse stored user:', e);
              localStorage.removeItem('supabase_user');
            }
          }
        }
        
        console.log('[AuthContext] Initial session:', { hasSession: !!data?.session, userId: user?.id });

        if (!mounted) return;
        setCurrentUser(user);
        if (user) {
          await fetchAndSetProfile(user);
        } else {
          setUserProfile(null);
          lastFetchedUserIdRef.current = null;
        }
      } catch (err) {
        console.error('[AuthContext] Init error:', err);
      } finally {
        setLoading(false);
      }
    };

    // Check localStorage on mount
    const checkStoredAuth = () => {
      const storedUser = localStorage.getItem('supabase_user');
      if (storedUser && !currentUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          console.log('[AuthContext] Restoring user from localStorage');
          setCurrentUser(parsedUser);
          const storedProfile = localStorage.getItem('supabase_profile');
          if (storedProfile) {
            try { 
              const parsedProfile = JSON.parse(storedProfile);
              setUserProfile(parsedProfile);
            } catch (_) {}
          }
          fetchAndSetProfile(parsedUser);
        } catch (e) {
          console.log('[AuthContext] Failed to parse stored user on mount:', e);
          localStorage.removeItem('supabase_user');
        }
      }
    };

    checkStoredAuth();

    const { data: listener } = supabase.auth.onAuthStateChange(async (event, session) => {
      const user = session?.user || null;
      console.log('[AuthContext] Auth state change:', { event, userId: user?.id });
      
      if (!mounted) return;
      
      setCurrentUser(user);
      if (user) {
        // Store user session in localStorage for persistence
        localStorage.setItem('supabase_user', JSON.stringify({
          id: user.id,
          email: user.email,
          user_metadata: user.user_metadata
        }));
        await fetchAndSetProfile(user);
      } else {
        // Clear localStorage when user signs out
        localStorage.removeItem('supabase_user');
        try { localStorage.removeItem('supabase_profile'); } catch (_) {}
        setUserProfile(null);
        lastFetchedUserIdRef.current = null;
      }
    });

    init();

    return () => {
      mounted = false;
      try {
        listener?.subscription?.unsubscribe && listener.subscription.unsubscribe();
      } catch (e) {
        // ignore
      }
    };
  }, [fetchAndSetProfile]);

  // Periodic authentication state check
  useEffect(() => {
    const checkAuthState = async () => {
      const storedUser = localStorage.getItem('supabase_user');
      if (storedUser && !currentUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          console.log('[AuthContext] Periodic check - restoring user');
          setCurrentUser(parsedUser);
          await fetchAndSetProfile(parsedUser);
        } catch (e) {
          console.log('[AuthContext] Periodic check - failed to parse stored user:', e);
          localStorage.removeItem('supabase_user');
        }
      }
      
      // Verify session is still valid
      if (currentUser) {
        try {
          const { data: session } = await supabase.auth.getSession();
          if (!session?.session) {
            console.log('[AuthContext] Periodic check - session expired');
            setCurrentUser(null);
            setUserProfile(null);
            localStorage.removeItem('supabase_user');
          }
        } catch (e) {
          console.log('[AuthContext] Periodic check - session verification failed:', e);
        }
      }
    };

    const interval = setInterval(checkAuthState, 5000);
    return () => clearInterval(interval);
  }, [currentUser, fetchAndSetProfile]);

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (error) {
      console.error('[AuthContext] Sign out error:', error);
      // Force local sign out even if Supabase call fails
      setCurrentUser(null);
      setUserProfile(null);
      localStorage.removeItem('supabase_user');
      try { localStorage.removeItem('supabase_profile'); } catch (_) {}
    }
  };

  const value = {
    currentUser,
    userProfile,
    loading,
    signOut,
    fetchAndSetProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
